import type { TypedDocumentNode } from '@apollo/client';
import { graphql } from '@newfront-insurance/graphql-frontend';

import type { GetChatsByFiltersChat } from './chats';

type ChatMutationVariables = {
  chatUuid: string;
};

type RenameChatMutationVariables = ChatMutationVariables & {
  title: string;
};

type RenameChatMutationData = {
  renameChat: GetChatsByFiltersChat;
};

type ArchiveChatMutationData = {
  archiveChat: GetChatsByFiltersChat;
};

type UnarchiveChatMutationData = {
  unarchiveChat: GetChatsByFiltersChat;
};

export const MARK_CHAT_AS_READ = graphql(`
  mutation MarkChatAsRead($chatUuid: String!) {
    markChatAsRead(chatUuid: $chatUuid) {
      uuid
      hasUnreadMessages
      activeStreamId
    }
  }
`);

export const RENAME_CHAT = graphql(`
  mutation RenameChat($chatUuid: String!, $title: String!) {
    renameChat(chatUuid: $chatUuid, title: $title) {
      uuid
      userUuid
      accountUuid
      account {
        uuid
        name
      }
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
    }
  }
`) as TypedDocumentNode<RenameChatMutationData, RenameChatMutationVariables>;

export const ARCHIVE_CHAT = graphql(`
  mutation ArchiveChat($chatUuid: String!) {
    archiveChat(chatUuid: $chatUuid) {
      uuid
      userUuid
      accountUuid
      account {
        uuid
        name
      }
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
    }
  }
`) as TypedDocumentNode<ArchiveChatMutationData, ChatMutationVariables>;

export const UNARCHIVE_CHAT = graphql(`
  mutation UnarchiveChat($chatUuid: String!) {
    unarchiveChat(chatUuid: $chatUuid) {
      uuid
      userUuid
      accountUuid
      account {
        uuid
        name
      }
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
    }
  }
`) as TypedDocumentNode<UnarchiveChatMutationData, ChatMutationVariables>;

export const DELETE_CHAT = graphql(`
  mutation DeleteChat($chatUuid: String!) {
    deleteChat(chatUuid: $chatUuid) {
      chatUuid
      deleted
    }
  }
`);

export const GET_NOVA_SETTINGS = graphql(`
  query GetNovaSettings($userUuid: String!) {
    getNovaSettings(userUuid: $userUuid) {
      notificationsEnabled
      outlookConnectEnabled
      outlookConnectedEmailGroupIds
      outlookSharedInboxes
    }
  }
`);

export const UPDATE_NOVA_SETTINGS = graphql(`
  mutation UpdateNovaSettings(
    $userUuid: String!
    $notificationsEnabled: Boolean
    $outlookConnectEnabled: Boolean
    $outlookConnectedEmailGroupIds: [String!]
    $outlookSharedInboxes: [String!]
  ) {
    updateNovaSettings(
      userUuid: $userUuid
      notificationsEnabled: $notificationsEnabled
      outlookConnectEnabled: $outlookConnectEnabled
      outlookConnectedEmailGroupIds: $outlookConnectedEmailGroupIds
      outlookSharedInboxes: $outlookSharedInboxes
    ) {
      notificationsEnabled
      outlookConnectEnabled
      outlookConnectedEmailGroupIds
      outlookSharedInboxes
    }
  }
`);

export const START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION = graphql(`
  mutation StartNovaMicrosoftGraphAuthorization($command: StartNovaMicrosoftGraphAuthorizationInput) {
    startNovaMicrosoftGraphAuthorization(command: $command) {
      authorizationUrl
      connectionUuid
      expiresAt
      state
    }
  }
`);

export const COMPLETE_NOVA_MICROSOFT_GRAPH_AUTHORIZATION = graphql(`
  mutation CompleteNovaMicrosoftGraphAuthorization($command: CompleteNovaMicrosoftGraphAuthorizationInput!) {
    completeNovaMicrosoftGraphAuthorization(command: $command) {
      connectionUuid
      status
    }
  }
`);

export const GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS = graphql(`
  query GetNovaMicrosoftGraphConnectionStatus {
    getNovaMicrosoftGraphConnectionStatus {
      connected
      connectionUuid
      externalUsername
      missingScopes
      needsReauth
      status
    }
  }
`);

export const ACQUIRE_NOVA_MICROSOFT_GRAPH_DELEGATED_TOKEN = graphql(`
  mutation AcquireNovaMicrosoftGraphDelegatedToken {
    acquireNovaMicrosoftGraphDelegatedToken {
      accessToken
      connectionUuid
      expiresAt
      missingScopes
      status
      tokenType
    }
  }
`);

export const DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION = graphql(`
  mutation DisconnectNovaMicrosoftGraphConnection {
    disconnectNovaMicrosoftGraphConnection {
      connected
      connectionUuid
      externalUsername
      missingScopes
      needsReauth
      status
    }
  }
`);

export const BUILD_CUSTOM_SKILL = graphql(`
  mutation BuildCustomSkill($command: BuildCustomSkillInput!) {
    buildCustomSkill(command: $command) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const CREATE_CUSTOM_SKILL = graphql(`
  mutation CreateCustomSkill($command: CreateCustomSkillInput!) {
    createCustomSkill(command: $command) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const UPDATE_CUSTOM_SKILL = graphql(`
  mutation UpdateCustomSkill($uuid: String!, $command: UpdateCustomSkillInput!) {
    updateCustomSkill: updateCustomSkill(uuid: $uuid, command: $command) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const SET_SHARED_CUSTOM_SKILL_ENABLED = graphql(`
  mutation SetSharedCustomSkillEnabled($uuid: String!, $command: SetSharedCustomSkillEnabledInput!) {
    setSharedCustomSkillEnabled: setSharedCustomSkillEnabled(uuid: $uuid, command: $command) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const DELETE_CUSTOM_SKILL = graphql(`
  mutation DeleteCustomSkill($uuid: String!) {
    deleteCustomSkill: deleteCustomSkill(uuid: $uuid)
  }
`);

export const UPDATE_CUSTOM_SKILL_DRAFT = graphql(`
  mutation UpdateCustomSkillDraft($uuid: String!, $command: UpdateCustomSkillInput!) {
    updateCustomSkillDraft(uuid: $uuid, command: $command) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const PUBLISH_CUSTOM_SKILL_DRAFT = graphql(`
  mutation PublishCustomSkillDraft($uuid: String!) {
    publishCustomSkillDraft(uuid: $uuid) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const SHARE_CUSTOM_SKILL_WITH_USER = graphql(`
  mutation ShareCustomSkillWithUser($uuid: String!, $command: ShareCustomSkillWithUserInput!) {
    shareCustomSkillWithUser(uuid: $uuid, command: $command)
  }
`);

export const UNSHARE_CUSTOM_SKILL_FROM_USER = graphql(`
  mutation UnshareCustomSkillFromUser($uuid: String!, $userUuid: String!) {
    unshareCustomSkillFromUser(uuid: $uuid, userUuid: $userUuid)
  }
`);
